import React, { useCallback, useMemo } from 'react'
import { WritableLexicalEditor } from '../../Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { EditorState } from 'lexical'
import { CommentPlugin } from '../../Lexical/Plugins/CommentPlugin/CommentPlugin'
import { AiResult, StructuredSummaryResult } from '../../../../../../../models/AiResult'
import { FormattedResultContentType } from '../../common'
import { getResultDisplayValues } from '../utils'
import { RichTextToolbarPlugin } from '../../Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { LEXICAL_NODES } from '../../Lexical/nodes'
import { getSafeValueOnPath } from '../../../utils'
import './EditableTextMeetingResult.scss'
import { getHtmlContentFromState } from '../../Lexical/utils'

interface EditableTextMeetingResultProps {
  aiResult: AiResult
  basePath: string
  path: string
  onResultTextContentChange: (basePath: string, path: string, resultTextContent: string | boolean) => void
  onFormattedResultChange: (basePath: string, path: string, contentType: string, content: string) => void
}

export const EditableTextMeetingResult: React.FC<EditableTextMeetingResultProps> = ({
  aiResult,
  basePath,
  path,
  onResultTextContentChange,
  onFormattedResultChange,
}) => {
  const result: StructuredSummaryResult = getSafeValueOnPath(aiResult.result, basePath)
  const richTextEnabled = typeof result.result === 'string'

  const { plainTextResult, htmlResult, lexicalResult } = useMemo(() => {
    return getResultDisplayValues(result, path)
  }, [result, path])

  const handleChange = useCallback(
    (editorState: EditorState) => {
      const editorStateString = JSON.stringify(editorState.toJSON())
      onFormattedResultChange(basePath, path, FormattedResultContentType.LEXICAL, editorStateString)
      onFormattedResultChange(basePath, path, FormattedResultContentType.HTML, getHtmlContentFromState(editorStateString))
    },
    [onFormattedResultChange, basePath, path],
  )

  const handleTextContentChange = useCallback(
    (textContent: string) => {
      onResultTextContentChange(basePath, path, textContent)
    },
    [onResultTextContentChange, basePath, path],
  )

  return (
    <WritableLexicalEditor
      editorContainerClassName='flex-grow-1'
      onChange={handleChange}
      onTextContentChange={handleTextContentChange}
      initialEditorState={lexicalResult}
      initialHtmlValue={htmlResult}
      initialTextValue={plainTextResult}
      lexicalNodes={LEXICAL_NODES}
      header={
        richTextEnabled ? (
          <RichTextToolbarPlugin className='px-1 zocks-py-0.75 gap-2'>
            <div className='editable-text-result-rich-text-divider' />
          </RichTextToolbarPlugin>
        ) : null
      }
    >
      <CommentPlugin aiResult={aiResult} basePath={basePath} path={path} />
    </WritableLexicalEditor>
  )
}
